<template>
  <div v-if="isOpen" class="modal-frame modal-large active shipment-import" @keydown="onKeydown">
    <div ref="dialog" class="modal-frame-content" role="dialog" aria-modal="true" :aria-label="t('title')" tabindex="-1" :aria-busy="busy">
      <button class="btn-close" type="button" :disabled="busy" :aria-label="t('close')" @click="close">
        <svg class="svg-cross"><use href="/img/sprite.svg#svg-cross" /></svg>
      </button>
      <div class="modal-frame-header"><h2 class="h3">{{ t('title') }}</h2></div>
      <div ref="body" class="modal-frame-body">
        <div v-if="!previewData && !session?.applyStarted" class="download-zone" @dragover.prevent @drop.prevent="selectFiles($event.dataTransfer.files)">
          <input v-if="!file" ref="input" type="file" accept=".xls,.xlsx" hidden @change="selectFiles($event.target.files); $event.target.value = ''">
          <div v-if="file" class="progress-group-list">
            <div class="progress-group">
              <div class="progress-title">
                <p class="font-16">{{ file.name }} <span class="muted-14">{{ file.size }} {{ t('bytes') }}</span></p>
                <button class="progress-cansel" type="button" :disabled="busy" :aria-label="t('removeFile')" @click.stop="removeFile">
                  <svg class="svg-cross"><use href="/img/sprite.svg#svg-cross" /></svg>
                </button>
              </div>
              <div class="progress-line-group">
                <div class="progress-status"><svg v-if="previewData" class="svg-check-fill"><use href="/img/sprite.svg#svg-check-fill" /></svg><div v-else class="font-11">{{ progress }}%</div></div>
                <div class="progress-line"><span :style="{ width: progress + '%' }" /></div>
              </div>
            </div>
          </div>
          <div class="download-zone-box"><div class="download-icon"><svg class="svg-load"><use href="/img/sprite.svg#svg-load" /></svg></div></div>
          <div v-if="!file" class="download-zone-text attachment-button">
            <p>{{ $t('uploads.dragFile') }} <a href="#!" class="txt-accent" @click.prevent="$refs.input.click()">{{ $t('uploads.chooseUpload') }}</a></p>
            <div class="download-zone-conditions"><span class="muted-13">{{ t('formats') }}</span></div>
          </div>
        </div>
        <div v-if="busy" class="mc-loader" aria-hidden="true"><div class="mc-loader__circle" /></div>
        <div role="status" aria-live="polite">
          <p v-if="phase === 'uploading'">{{ t('uploading') }} {{ progress }}%</p>
          <p v-if="phase === 'applying'">{{ t('applying') }}</p>
          <p v-if="phase === 'cancelling'">{{ t('cancelling') }}</p>
        </div>
        <div v-if="errors.length" class="error-message" role="alert"><p v-for="(error, i) in errors" :key="i">{{ error }}</p></div>
        <button v-if="cancelFailed && !busy" class="btn-outline" type="button" @click="closeUnconfirmed">{{ t('closeUnconfirmed') }}</button>
        <template v-if="previewData">
          <h3>{{ result ? t(result.state === 'interrupted' ? 'interrupted' : result.summary?.outcome || 'rejected') : session?.applyStarted ? t('result') : t('preview') }}</h3>
          <p v-if="result?.summary?.errors?.length" class="error-message" role="alert">{{ result.summary.errors.map(error => error.message).join('; ') }}</p>
          <p v-else-if="hasRowErrors && !tableExpanded" class="error-message">{{ t('tableErrors') }}</p>
          <dl class="shipment-counts"><div v-for="key in countKeys" :key="key"><dt>{{ t(key) }}</dt><dd>{{ displayCounts[key] ?? 0 }}</dd></div></dl>
          <button class="filters-toggle link-dotted-icon" type="button" :aria-expanded="tableExpanded" aria-controls="shipment-preview-table" @click="tableExpanded = !tableExpanded">
            <svg class="filters-toggle__icon" aria-hidden="true"><use :href="`/img/sprite.svg#${tableExpanded ? 'svg-up' : 'svg-down'}`" /></svg>
            <span class="font-14">{{ t(tableExpanded ? 'hideTable' : 'showTable') }}</span>
          </button>
          <div v-show="tableExpanded" id="shipment-preview-table" class="shipment-preview-scroll" tabindex="0" :aria-label="t('preview')">
            <table class="shipment-preview-table">
              <thead><tr><th v-for="field in fileFields" :key="field" scope="col">{{ t('fileColumns.' + field) }}</th><th scope="col">{{ t('rowStatus') }}</th><th v-if="hasRowErrors" scope="col">{{ t('rowError') }}</th></tr></thead>
              <tbody><tr v-for="row in displayRows" :key="row.excelRow">
                <td v-for="field in fileFields" :key="field">{{ cellText(row.values?.[field], field) }}</td>
                <td>{{ t(session?.applyStarted && !result ? 'unknown' : result ? row.status : expired ? 'rejected' : row.status) }}</td>
                <td v-if="hasRowErrors">{{ rowErrors(row) }}</td>
              </tr><tr v-if="!displayRows.length"><td :colspan="fileFields.length + 2">{{ (result?.summary?.errors || previewData?.errors || []).map(error => error.message).join('; ') }}</td></tr></tbody>
            </table>
          </div>
          <div ref="dates" :style="{ '--calendar-space': calendarSpace + 'px' }" class="form-row shipment-common-dates" :class="{ 'shipment-common-dates--editable': !session?.applyStarted }">
            <div v-for="field in dateFields" :key="field" class="form-item form-calendar">
              <label class="calendar-label" :for="'dp-input-shipment-' + field">{{ t('fileColumns.' + field) }}</label>
              <div class="calendar-field">
                <Datepicker :model-value="commonDates[field]" @update:model-value="updateDate(field, $event)"
                  :uid="'shipment-' + field" class="calendar-input" :placeholder="$t('modals.noChange')"
                  :enable-time-picker="false" auto-position="top" @open="positionCalendar(field)" auto-apply format="dd.MM.yyyy" model-type="yyyy-MM-dd"
                  :locale="$i18n.locale" :disabled="busy || session?.applyStarted || previewData.state !== 'preview'"
                  :aria-labels="{ input: t('fileColumns.' + field) }" />
                <span class="icon-calendar"></span>
              </div>
              <p v-for="(error, index) in dateErrors(field)" :key="index" class="error-message" role="alert">{{ error.message }}</p>
            </div>
          </div>
          <p v-if="!session?.applyStarted && !hasDates">{{ t('chooseDates') }}</p>
          <p v-if="recalculating" role="status">{{ t('recalculating') }}</p>
          <button v-if="recalculationFailed && !session?.applyStarted" class="btn-outline" type="button" :disabled="busy || recalculating" @click="recalculate">{{ t('recalculate') }}</button>
        </template>
        <template v-if="phase === 'uncertain'">
          <p role="alert">{{ t('uncertain') }}</p>
          <p>{{ t('attempt') }}: {{ session.attemptId }}</p>
          <button class="btn-outline" type="button" @click="apply">{{ t('retry') }}</button>
        </template>
      </div>
      <div class="modal-footer">
        <template v-if="previewData && !session?.applyStarted">
          <button class="btn" type="button" :disabled="busy || recalculating || dateRevision !== previewData.revision || !previewData.canApply || expired" @click="apply">{{ t('apply') }}</button>
        </template>
        <button v-if="(previewData && !session?.applyStarted) || result" class="btn-outline" type="button" :disabled="busy" @click="anotherFile">{{ t('anotherFile') }}</button>
        <button class="btn-outline" type="button" :disabled="busy" @click="close">{{ t('close') }}</button></div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { translateApiError } from '@/utils/apiErrorUtils';
export default {
  components: { Datepicker },
  props: { isOpen: Boolean, reportDetachedCleanupError: { type: Function, default: null }, scope: { type: Object, required: true } },
  emits: ['close', 'completed', 'cleanup-error'],
  data: () => ({ calendarSpace: 340, layoutObserver: null, layoutFrame: null, phase: 'file', file: null, previewData: null, result: null, errors: [], progress: 0, cancelFailed: false, detached: false, session: null, clock: Date.now(), timer: null, previousFocus: null, tableExpanded: false, commonDates: { plannedDate: null, readyDate: null, shipmentDate: null }, dateRevision: 0, recalculating: false, recalculationFailed: false }),
  computed: {
    busy() { return ['uploading', 'applying', 'cancelling'].includes(this.phase); },
    expired() { return !!this.previewData?.expiresAt && Date.parse(this.previewData.expiresAt) <= this.clock; },
    countKeys() { return this.result ? ['totalRows', 'processed', 'updated', 'created', 'skipped'] : ['totalRows', 'validRows', 'foundPositions', 'applicablePositions', 'errorRows']; },
    displayCounts() { return this.result ? { totalRows: this.previewData?.counts?.totalRows, ...this.result.summary?.counters } : this.previewData?.counts || {}; },
    fileFields() { return ['invoice', 'article', 'quantity']; },
    dateFields() { return ['plannedDate', 'readyDate', 'shipmentDate']; },
    hasDates() { return Object.values(this.commonDates).some(Boolean); },
    previewRows() { return this.previewData?.rows || []; },
    displayRows() {
      if (!this.result) return this.previewRows;
      return this.previewRows.map(row => {
        const saved = (this.result.positions || []).find(position => position.plan?.excelRows?.includes(row.excelRow));
        const applied = saved?.status === 'applied';
        let errors = applied ? [] : saved?.errors?.length ? saved.errors : row.errors || [];
        if (!applied && !errors.length) errors = this.result.summary?.errors || [];
        return { ...row, status: applied ? 'applied' : saved || this.result.state === 'rejected' || row.status === 'rejected' ? 'rejected' : 'skipped', errors };
      });
    },
    hasRowErrors() { return (!this.result && this.expired) || this.displayRows.some(row => row.errors?.length) || !!(this.result?.summary?.errors || this.previewData?.errors)?.length; }
  },
  watch: { phase() { this.$nextTick(() => { const dialog = this.$refs.dialog; if (this.isOpen && dialog && (!dialog.contains(document.activeElement) || document.activeElement?.disabled)) dialog.focus(); }); }, isOpen: { immediate: true, handler(open) { if (open) this.start(); else this.release(); } } },
  beforeUnmount() { this.release(); this.detached = true; },
  methods: {
    t(key, values) { return this.$t(`shipmentImport.${key}`, values || {}); },
    cellText(value, field) {
      if (value == null) return '';
      if (field.endsWith('Date') && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) return value.split('-').reverse().join('.');
      return String(value);
    },
    rowErrors(row) {
      if (!this.result && this.expired) return this.t('expired');
      return [...new Set((row.errors || []).map(issue => issue.message))].join('; ');
    },
    start() {
      this.release();
      this.session = { closed: false, attemptId: null, applyStarted: false, emitted: false };
      this.phase = 'file'; this.file = this.previewData = this.result = null; this.errors = []; this.progress = 0; this.cancelFailed = false;
      this.resetDates();
      this.previousFocus = document.activeElement;
      this.clock = Date.now(); this.timer = setInterval(() => { this.clock = Date.now(); }, 1000);
      this.$nextTick(() => { if (this.isOpen) { this.$refs.dialog?.focus(); this.observeLayout(); } });
    },
    observeLayout() {
      this.layoutObserver = new ResizeObserver(() => this.scheduleLayout());
      [this.$refs.dialog, this.$refs.body].filter(Boolean).forEach(node => this.layoutObserver.observe(node));
      window.addEventListener('resize', this.scheduleLayout);
      this.scheduleLayout();
    },
    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        const dialog = this.$refs.dialog, dates = this.$refs.dates;
        if (!dialog || !dates || this.session?.applyStarted) return;
        const input = dates.querySelector('input');
        if (!input) return;
        // Reserve only the missing space ABOVE the first calendar; ignore the existing reserve.
        const reserve = parseFloat(getComputedStyle(dates).paddingTop) || 0;
        const naturalTop = input.getBoundingClientRect().top - dialog.getBoundingClientRect().top + dialog.scrollTop - reserve;
        this.calendarSpace = Math.max(0, Math.ceil(350 - naturalTop));
      });
    },
    positionCalendar(field) {
      this.$nextTick(() => requestAnimationFrame(() => {
        const dialog = this.$refs.dialog;
        const input = dialog?.querySelector('#dp-input-shipment-' + field);
        if (!input) return;
        const menu = dialog.querySelector('.dp__menu');
        const height = menu?.getBoundingClientRect().height || 320;
        const targetTop = Math.min(dialog.clientHeight - input.offsetHeight - 16, height + 20);
        const offset = input.getBoundingClientRect().top - dialog.getBoundingClientRect().top;
        dialog.scrollTop += offset - targetTop;
      }));
    },
    active(session) { return this.isOpen && this.session === session && !session.closed; },
    complete(session) {
      if ((!session.applyStarted && !session.uncertainCancellation) || session.emitted) return;
      session.emitted = true;
      this.$emit('completed', { attemptId: session.attemptId, refreshNeeded: true, uncertain: !session.result, result: session.result || null });
    },
    release() {
      this.layoutObserver?.disconnect(); this.layoutObserver = null;
      cancelAnimationFrame(this.layoutFrame);
      window.removeEventListener('resize', this.scheduleLayout);
      clearInterval(this.timer); this.timer = null;
      const session = this.session;
      if (!session || session.closed) return;
      session.closed = true;
      this.complete(session);
      if (!session.applyStarted && !session.uncertainCancellation && session.attemptId && !session.cancelled) this.cancel(session).catch(error => this.cleanupError(session, error));
      this.previousFocus?.focus?.();
    },
    cleanupError(session, error) {
      const payload = { attemptId: session.attemptId, errors: this.messages(error) };
      // Vue drops emits after unmount. A host callback can report a late cancellation failure.
      if (this.detached) this.reportDetachedCleanupError?.(payload);
      else this.$emit('cleanup-error', payload);
    },
    async request(action, data, config = {}) {
      const form = new FormData();
      Object.entries(data).forEach(([key, value]) => form.append(key, String(value)));
      if (data.file) form.set('file', data.file);
      const response = await api.post(`/api/shipment-import/${action}/`, form, config);
      if (response.data?.status !== 'success') throw { issues: response.data?.errors };
      return response.data.data;
    },
    messages(error) {
      const issues = error.issues || error.response?.data?.errors;
      return Array.isArray(issues) && issues.length ? issues.map(issue => translateApiError(issue, this.$t)) : [this.t('network')];
    },
    async cancel(session) {
      if (session.cancelled || !session.attemptId || session.applyStarted) return;
      if (!session.cancelling) session.cancelling = this.request('cancel', { attemptId: session.attemptId }).then(data => {
        if (data.state !== 'cancelled') throw { issues: [{ code: 'invalid_state', message: this.t('cancelFailed') }] };
        session.cancelled = true;
      }).finally(() => { session.cancelling = null; });
      return session.cancelling;
    },
    async selectFiles(files) {
      if (this.file || this.busy || this.session?.applyStarted || !files?.length) return;
      if (files.length !== 1 || !/\.xlsx?$/i.test(files[0].name) || !files[0].size || files[0].size > 5 * 1024 * 1024) { this.errors = [this.t('invalidFile')]; return; }
      this.file = files[0]; this.errors = []; this.progress = 0;
      await this.preview();
    },
    async anotherFile() {
      if (this.busy) return;
      if (this.result) {
        this.start();
        this.$nextTick(() => this.$refs.dialog?.querySelector('.attachment-button a')?.focus());
      } else await this.removeFile();
    },
    async removeFile() {
      if (this.busy || this.session?.applyStarted || !this.file) return;
      const session = this.session;
      this.phase = 'cancelling'; this.errors = [];
      try { await this.cancel(session); }
      catch (error) {
        if (this.active(session)) { this.errors = this.messages(error); this.cancelFailed = true; this.phase = 'preview'; }
        return;
      }
      if (!this.active(session)) return;
      session.attemptId = null; session.cancelled = false;
      this.file = this.previewData = null; this.progress = 0; this.cancelFailed = false; this.phase = 'file'; this.resetDates();
      this.$nextTick(() => this.$refs.dialog?.querySelector('.attachment-button a')?.focus());
    },
    async preview() {
      if (this.phase !== 'file' || !this.file) return;
      if (!['order', 'positions'].includes(this.scope.page) || (this.scope.page === 'order' && !this.scope.orderId)) { this.errors = [this.t('invalidScope')]; return; }
      const session = this.session; this.phase = 'uploading'; this.errors = [];
      try {
        const data = await this.request('preview', { file: this.file, page: this.scope.page, ...(this.scope.page === 'order' ? { orderId: this.scope.orderId } : {}) }, { onUploadProgress: event => { if (this.active(session) && event.total) this.progress = Math.round(event.loaded / event.total * 100); } });
        session.attemptId = data.attemptId;
        session.cancelled = data.state === 'rejected';
        if (!this.active(session)) { await this.cancel(session); return; }
        this.previewData = data; this.progress = 100; this.phase = 'preview';
      } catch (error) { if (this.active(session)) { this.errors = this.messages(error); this.phase = 'file'; } else this.cleanupError(session, error); }
    },
    resetDates() {
      this.commonDates = { plannedDate: null, readyDate: null, shipmentDate: null };
      this.dateRevision = 0; this.recalculating = false; this.recalculationFailed = false; this.tableExpanded = false;
    },
    dateErrors(field) { return (this.previewData?.dateErrors || []).filter(error => error.field === field); },
    updateDate(field, value) {
      if (this.busy || this.session?.applyStarted) return;
      this.commonDates[field] = value || null;
      this.recalculate();
    },
    async recalculate() {
      if (!this.session?.attemptId || this.session.applyStarted || this.busy) return;
      const session = this.session, revision = ++this.dateRevision;
      const dates = Object.fromEntries(Object.entries(this.commonDates).map(([key, value]) => [key, value || '']));
      this.recalculating = true; this.recalculationFailed = false; this.errors = [];
      try {
        const data = await this.request('recalculate', { attemptId: session.attemptId, revision, ...dates });
        if (!this.active(session) || session.applyStarted || revision !== this.dateRevision) return;
        if (data.revision !== revision) throw new Error('Unexpected preview revision');
        this.previewData = data;
      } catch (error) {
        if (this.active(session) && !session.applyStarted && revision === this.dateRevision) { this.errors = this.messages(error); this.recalculationFailed = true; }
      } finally {
        if (this.active(session) && revision === this.dateRevision) this.recalculating = false;
      }
    },
    async apply() {
      if (this.busy || !this.session?.attemptId || !['preview', 'uncertain'].includes(this.phase)) return;
      if (this.phase === 'preview' && (!this.previewData?.canApply || this.expired || this.recalculating || this.dateRevision !== this.previewData.revision)) return;
      const session = this.session; session.confirmedRevision ??= this.previewData.revision; session.applyStarted = true; this.phase = 'applying'; this.errors = [];
      try {
        const data = await this.request('apply', { attemptId: session.attemptId, revision: session.confirmedRevision });
        session.result = data;
        if (this.active(session)) { this.result = data; this.phase = 'result'; if (this.hasRowErrors || data.summary?.outcome !== 'success' || data.state === 'interrupted') this.tableExpanded = true; }
      } catch (error) { if (this.active(session)) { this.errors = this.messages(error); this.phase = 'uncertain'; this.tableExpanded = true; } }
    },
    closeUnconfirmed() {
      if (this.busy || !this.cancelFailed) return;
      this.session.uncertainCancellation = true;
      this.release(); this.$emit('close');
    },
    async close() {
      if (this.busy) return;
      const session = this.session;
      if (!session.applyStarted && session.attemptId && !session.cancelled) {
        this.phase = 'cancelling'; this.errors = [];
        try { await this.cancel(session); } catch (error) { if (this.active(session)) { this.errors = this.messages(error); this.cancelFailed = true; this.phase = 'preview'; } return; }
      }
      if (!this.active(session)) return;
      this.release(); this.$emit('close');
    },
    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
      if (event.key !== 'Tab') return;
      const nodes = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), a[href], summary, input:not([hidden]):not(:disabled), [tabindex="0"]')].filter(node => node.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === this.$refs.dialog)) { event.preventDefault(); first.focus(); }
    }
  }
};
</script>
<style scoped>
.shipment-import { position: fixed; inset: 0; z-index: 1100; overflow-y: auto; padding: 20px; background: #0006; }
.shipment-import .modal-frame-content { position: relative; margin: auto; width: 100%; max-width: 1100px; max-height: calc(100dvh - 40px); overflow-y: auto; overflow-wrap: anywhere; padding: 32px; min-width: 0; }
.shipment-import .modal-frame-body { overflow: visible; }
.shipment-import :deep(.shipment-counts) { display: flex; gap: 8px 16px; flex-wrap: wrap; margin: 16px 0; }
.shipment-import :deep(.shipment-counts dd) { margin: 0; }
.shipment-counts > div { display: flex; flex-direction: column; gap: 6px; }
.shipment-preview-scroll { overflow-x: auto; margin: 20px 0; }
.shipment-preview-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.shipment-preview-table th, .shipment-preview-table td { padding: 12px; border: 1px solid #ddd; text-align: left; vertical-align: top; min-width: 110px; }
.shipment-preview-table th { background: #f3f6f6; }
.shipment-common-dates { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-top: 20px; }
.shipment-common-dates--editable { padding-top: var(--calendar-space, 340px); }
.shipment-common-dates .form-item { display: grid; grid-template-rows: subgrid; grid-row: span 3; row-gap: 8px; width: auto; min-width: 0; margin: 0; }
.shipment-common-dates .calendar-label { align-self: end; margin: 0; }
.shipment-common-dates .calendar-field { align-self: start; width: 100%; }
.shipment-common-dates :deep(.dp__menu) { z-index: 1200; }
.shipment-import .modal-footer { display: flex; flex-wrap: wrap; gap: 12px; }
.shipment-import button:disabled { opacity: .5; cursor: wait; }
.shipment-import :deep(:focus-visible) { outline: 2px solid currentColor; outline-offset: 3px; }
.shipment-import p { margin-bottom: 12px; }
.shipment-import h3, .shipment-import h4 { margin: 16px 0 8px; }
@media (max-width: 600px) { .shipment-common-dates { grid-template-columns: 1fr; } .shipment-import .modal-frame-content { padding: 48px 16px 20px; } }
@media (prefers-reduced-motion: reduce) { .shipment-import .modal-frame-content { animation: none; opacity: 1; } .shipment-import .mc-loader__circle { animation: none; } }
</style>
